import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _PublicationService from 'modules/PublicationService';
import _notification from 'modules/notification';
import _utils from 'modules/Utils';
import _branding from 'modules/branding';
import { buildSelectedWaitingListPdf } from 'modules/SelectedWaitingListPdf';
import { Card, Modal, Icon } from 'antd';
import PageHeader from 'components/enterprise/PageHeader';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import EmptyState from 'components/enterprise/EmptyState';
import DropdownList from 'components/controls/DropdownList';
import ButtonComponent from 'components/controls/Button';
import FileUpload from '../../components/controls/FileUpload';
import ConfirmPopup from '../popup/ConfirmPopup';
import { routesAdmin } from 'routes/Routes';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

// Publication type for the combined Roll-Number-wise Selected/Waiting PDF — reuses the pre-existing,
// previously-unused PublicationService.TYPE_SELECTED_AND_WAITING constant/allow-list entry
// ("SelectedAndWaitingList") verbatim; deliberately ONE document, never split into separate
// Selected/Waiting publications (matches the real sample document's own combined structure).
const PUBLICATION_TYPE_SELECTED_WAITING = 'SelectedAndWaitingList';
const titleForSelectedWaitingPost = (postName) => 'Selected & Waiting List - ' + postName;

// Interview Marks Workflow & Final Result Foundation phase.
//
// Explicit staged workflow, per the phase document's own required pattern:
//   Import (stages PendingResult/PendingStudentMessage only) -> Preview -> Admin Review ->
//   Confirm Publish (copies Pending* into the live Result/StudentMessage, activates candidate display)
// Nothing is ever visible to candidates merely because a file was uploaded — only the explicit
// Publish action activates it, and Publish itself refuses to silently replace an already-published
// result without confirmation (mirrors the Interview Shortlist screen's exact pattern).
//
// Final Result, Selected/Waiting Publication & Appointment Letter Closure phase: the Final Status
// breakdown shown below reflects each candidate's authoritative, structured PendingFinalStatus —
// decided once at Import time (either from an explicit FinalStatus column in the uploaded file, or
// derived server-side as a fallback from the same already-established StudentMessage vocabulary) —
// never a UI-side re-derivation from message text. No final-merit formula, weighting, or tie-break
// rule is implemented here; the real outcome is always the human-decided Result/StudentMessage/
// FinalStatus, entered by the admin/entity via the uploaded Final Result file. The Waiting Target
// (20% of Vacancy) is validation/warning-only — it never alters who is actually Selected/Waiting.
class FinalResult extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            posts: [],
            selectedPostId: null,
            preview: null,
            previewLoading: false,
            uploadedDoc: "",
            fileError: "",
            uploading: false,
            uploadErrorMessage: "",
            showConfirm: false,
            publishing: false,
            // Final Result, Selected/Waiting Publication & Appointment Letter Closure phase.
            publications: [],
            portalConfig: {},
            generatingPdf: false,
            publishingPdf: false,
            rollNumbers: null,
            pdfBlob: null,
            pdfFileName: '',
            pdfGeneratedAt: null,
            showPdfPublishConfirm: false
        };
    }

    componentDidMount() {
        this.loadPosts();
        this.loadPublicationContext();
    }

    loadPosts = async () => {
        this.setState({ loading: true });
        try {
            const response = await _AdminService.GetAllPost();
            this.setState({ posts: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    loadPublicationContext = async () => {
        try {
            const [publicationsResp, portalConfigResp] = await Promise.all([
                _PublicationService.getAll(),
                _AdminService.GetPortalConfigurationWithoutImage()
            ]);
            this.setState({ publications: publicationsResp.data || [], portalConfig: portalConfigResp.data || {} });
        } catch (e) { /* non-fatal; Selected/Waiting PDF publish section degrades gracefully */ }
    }

    refreshPublications = async () => {
        try {
            const response = await _PublicationService.getAll();
            this.setState({ publications: response.data || [] });
        } catch (e) { /* non-fatal; existing list stays stale until next reload */ }
    }

    // The Published, active Selected/Waiting record for the currently-selected post, if any.
    getExistingSelectedWaitingPublication = () => {
        const post = this.getSelectedPost();
        if (!post) return null;
        const title = titleForSelectedWaitingPost(post.Name);
        return (this.state.publications || []).find(p =>
            p.PublicationType === PUBLICATION_TYPE_SELECTED_WAITING &&
            p.Title === title &&
            p.Status === 'Published' &&
            p.IsActive
        ) || null;
    }

    getSelectedPost = () => (this.state.posts || []).find(p => p.Id === this.state.selectedPostId) || null;

    onPostChange = (e) => {
        const postId = parseInt(e.target.value, 10) || null;
        this.setState({
            selectedPostId: postId, preview: null,
            rollNumbers: null, pdfBlob: null, pdfFileName: '', pdfGeneratedAt: null
        }, () => {
            if (postId) {
                this.loadPreview();
            }
        });
    }

    loadPreview = async () => {
        this.setState({ previewLoading: true });
        try {
            const response = await _AdminService.PreviewFinalResult(this.state.selectedPostId);
            if (response.status === 200 && response.data.IsSuccess) {
                this.setState({ preview: response.data.Obj, previewLoading: false });
            } else {
                this.setState({ previewLoading: false });
                _notification.showMessage('error', response.data.Message || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ previewLoading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onFileDrop = (file) => {
        this.setState({ uploadedDoc: file, fileError: '', uploadErrorMessage: "" });
    }

    onFileError = (errorMsg) => {
        this.setState({ fileError: errorMsg });
    }

    handleUpload = async () => {
        if (!this.state.selectedPostId) {
            _notification.showMessage('error', 'Select a Post first.');
            return;
        }
        if (!this.state.uploadedDoc || this.state.fileError !== "") {
            _notification.showMessage('error', 'Select a valid file to upload.');
            return;
        }
        this.setState({ uploading: true });
        try {
            const formData = new FormData();
            formData.append('file', this.state.uploadedDoc);
            const response = await _AdminService.ImportFinalResult(this.state.selectedPostId, formData);
            if (response.status === 200 && response.data.IsSuccess) {
                _notification.showMessage('success', response.data.Message || 'Final Result staged. Review the preview below, then publish when ready.');
                this.setState({ uploading: false, uploadedDoc: "", uploadErrorMessage: "" });
                this.loadPreview();
            } else {
                this.setState({ uploading: false, uploadErrorMessage: response.data.Message || '' });
                _notification.showMessage('error', response.data.Message || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ uploading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    openConfirm = () => {
        if (!this.state.preview || this.state.preview.PendingCount === 0) {
            _notification.showMessage('error', 'Import a Final Result file for this Post before publishing.');
            return;
        }
        this.setState({ showConfirm: true });
    }

    handlePublish = async () => {
        this.setState({ showConfirm: false, publishing: true });
        try {
            const response = await _AdminService.PublishFinalResult(this.state.selectedPostId, this.state.preview.AlreadyPublishedCount > 0);
            if (response.status === 200 && response.data.IsSuccess) {
                this.setState({ publishing: false, preview: response.data.Obj });
                _notification.showMessage('success', `Final Result published for ${response.data.Obj.PostName}.`);
            } else {
                this.setState({ publishing: false });
                _notification.showMessage('error', response.data.Message || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ publishing: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    // Final Result, Selected/Waiting Publication & Appointment Letter Closure phase.
    //
    // Flow: Final Result -> Selected/Waiting validation -> Generate PDF -> Admin Preview -> Admin
    // Publish -> Portal Home Page. Roll Numbers are read from the already-PUBLISHED FinalStatus only
    // (GetPublishedSelectedWaitingRollNumbers) — never from the staged/Pending preview above — so a
    // PDF can never be generated ahead of an actual Final Result publication for this Post.
    handleGenerateSelectedWaitingPdf = async () => {
        const post = this.getSelectedPost();
        if (!post) {
            _notification.showMessage('error', 'Please select a Post first.');
            return;
        }
        if (!this.state.preview || this.state.preview.AlreadyPublishedCount === 0) {
            _notification.showMessage('error', 'Publish the Final Result for this Post before generating the Selected/Waiting list.');
            return;
        }

        // Opened synchronously, inside the click's own user-gesture window — matches the Eligible
        // Candidate List screen's own proven fix for Chrome's popup blocker.
        const previewWindow = window.open('', '_blank');

        this.setState({ generatingPdf: true });
        try {
            const response = await _AdminService.GetPublishedSelectedWaitingRollNumbers(post.Id);
            if (!(response.status === 200 && response.data && response.data.IsSuccess)) {
                if (previewWindow) { previewWindow.close(); }
                this.setState({ generatingPdf: false });
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
                return;
            }
            const rollNumbers = response.data.Obj;
            if ((rollNumbers.SelectedRollNumbers || []).length === 0 && (rollNumbers.WaitingRollNumbers || []).length === 0) {
                if (previewWindow) { previewWindow.close(); }
                this.setState({ generatingPdf: false, rollNumbers, pdfBlob: null });
                _notification.showMessage('error', 'No published Selected or Waiting candidates found for this Post. Nothing to publish yet.');
                return;
            }

            const generatedAt = new Date();
            const entityName = _branding.resolveOrganizationName(this.state.portalConfig.Exam_Name || '');
            const doc = buildSelectedWaitingListPdf({
                entityName,
                recruitmentLabel: this.state.portalConfig.Exam_Name,
                postName: post.Name,
                selectedRollNumbers: rollNumbers.SelectedRollNumbers,
                waitingRollNumbers: rollNumbers.WaitingRollNumbers,
                generatedAt
            });
            const pdfBlob = doc.output('blob');
            const pdfFileName = _utils.buildSelectedWaitingListFileName(post.Name, generatedAt);

            const previewUrl = URL.createObjectURL(pdfBlob);
            if (previewWindow) {
                previewWindow.location.href = previewUrl;
            } else {
                window.open(previewUrl, '_blank');
            }

            this.setState({ generatingPdf: false, rollNumbers, pdfBlob, pdfFileName, pdfGeneratedAt: generatedAt });
            _notification.showMessage('success', `Selected/Waiting list generated for ${post.Name} (${rollNumbers.SelectedRollNumbers.length} Selected, ${rollNumbers.WaitingRollNumbers.length} Waiting). Review the preview, then Publish when ready.`);
        } catch (e) {
            if (previewWindow) { previewWindow.close(); }
            this.setState({ generatingPdf: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleRePreviewSelectedWaitingPdf = () => {
        if (!this.state.pdfBlob) return;
        const url = URL.createObjectURL(this.state.pdfBlob);
        window.open(url, '_blank');
    }

    openSelectedWaitingPublishConfirm = () => {
        if (!this.state.pdfBlob) {
            _notification.showMessage('error', 'Generate and preview the Selected/Waiting list before publishing.');
            return;
        }
        this.setState({ showPdfPublishConfirm: true });
    }

    handlePublishSelectedWaitingPdf = async () => {
        this.setState({ showPdfPublishConfirm: false, publishingPdf: true });
        const post = this.getSelectedPost();
        const existing = this.getExistingSelectedWaitingPublication();

        try {
            const file = new File([this.state.pdfBlob], this.state.pdfFileName, { type: 'application/pdf' });
            const model = {
                PublicationType: PUBLICATION_TYPE_SELECTED_WAITING,
                Title: titleForSelectedWaitingPost(post.Name),
                Description: `Selected & Waiting Candidates List for ${post.Name} — generated ${moment(this.state.pdfGeneratedAt).format('DD-MMM-YYYY HH:mm')} ` +
                    `(${this.state.rollNumbers.SelectedRollNumbers.length} Selected, ${this.state.rollNumbers.WaitingRollNumbers.length} Waiting) from the published Final Result.`,
                PublicationLanguage: 'en',
                PublicationDate: moment().format('YYYY-MM-DDT00:00:00'),
                EffectiveDate: '',
                DisplayOrder: 0
            };

            let response;
            if (existing) {
                response = await _PublicationService.supersede(existing.Id, model, file);
            } else {
                response = await _PublicationService.create(model, file);
                if (response.status === 200 && response.data && response.data.Id) {
                    response = await _PublicationService.publish(response.data.Id);
                }
            }

            if (response.status === 200) {
                _notification.showMessage('success', `Selected & Waiting Candidates List published for ${post.Name}.`);
                this.setState({ publishingPdf: false, rollNumbers: null, pdfBlob: null, pdfFileName: '', pdfGeneratedAt: null });
                this.refreshPublications();
            } else {
                this.setState({ publishingPdf: false });
                _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ publishingPdf: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    render() {
        const { loading, posts, preview, previewLoading, uploading, publishing, rollNumbers, pdfBlob, generatingPdf, publishingPdf } = this.state;

        if (loading) return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Final Result"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Candidates' }, { label: 'Final Result' }]}
                />
                <div className="max1200 m-auto">
                    <Card>
                        {posts.length === 0 ? (
                            <EmptyState icon="team" title="No Posts Found" description="Create a Post/Exam before preparing a Final Result." />
                        ) : (
                            <>
                                <div className="row">
                                    <div className="col-sm-6 mb-3">
                                        <DropdownList
                                            value={this.state.selectedPostId || 0}
                                            name="postId"
                                            onChange={this.onPostChange}
                                            placeholder="Select Post"
                                            heading="Post/Exam"
                                            className="w-100"
                                            wrapClass="wrap"
                                            isRequired={true}
                                            options={posts}
                                        />
                                    </div>
                                </div>

                                {this.state.selectedPostId && (
                                    <div className="mb-3">
                                        <a className="button-global button-border addbutton autoWidth d-inline-block text-center autoHeight mb-3 d-flex align-items-center justify-content-center"
                                            href={require('../../assets/template/StudentInterviewResultData.xlsx')} download="FinalResultData.xlsx">
                                            <Icon type="download" className="mr-2 font1rem" /> Download Template (same format as Interview Marks)
                                        </a>
                                        <div className="mb-2 col-sm-12 uploadCard">
                                            <FileUpload onFileDrop={this.onFileDrop} onError={this.onFileError} />
                                            {this.state.fileError ? <label className="textAlignCenter"><span className="color-red">Error: {this.state.fileError}</span></label> : <></>}
                                        </div>
                                        {this.state.uploadErrorMessage !== "" && (
                                            <Card className="mb-2 col-sm-12 pb-3">
                                                <span className='color-red' dangerouslySetInnerHTML={{ __html: this.state.uploadErrorMessage }}></span>
                                            </Card>
                                        )}
                                        <ButtonComponent
                                            type="button"
                                            value={uploading ? 'Staging…' : 'Import Final Result (Stage Only)'}
                                            onClick={this.handleUpload}
                                            className="button-global button-primary autoHeight small"
                                            disabled={uploading}
                                        />
                                    </div>
                                )}

                                {previewLoading && <LoadingSpinner />}

                                {!previewLoading && preview && (
                                    <div className="mb-3 pt-3" style={{ borderTop: '1px solid #eee' }}>
                                        <p><b>Post:</b> {preview.PostName}</p>
                                        <p><b>Vacancy:</b> {preview.Vacancy != null ? preview.Vacancy : '-'} &nbsp;&middot;&nbsp; <b>Waiting Target (20% of Vacancy):</b> {preview.WaitingTarget != null ? preview.WaitingTarget : '-'}</p>
                                        <p><b>Total Candidates:</b> {preview.TotalCandidates}</p>
                                        <p><b>Interview Candidates:</b> {preview.InterviewCandidates}</p>
                                        <p><b>Interview Marks Received:</b> {preview.InterviewMarksReceived} ({preview.MissingInterviewMarks} missing)</p>
                                        <p><b>Staged (Not Yet Published):</b> {preview.PendingCount}</p>
                                        <p><b>Already Published:</b> {preview.AlreadyPublishedCount}</p>
                                        <p>
                                            <b>Staged Final Status breakdown:</b>{' '}
                                            Selected: {preview.SelectedCount}, Waiting: {preview.WaitingCount}, Not Selected: {preview.NotSelectedCount}, Absent: {preview.AbsentCount}, Disqualified: {preview.DisqualifiedCount}, Unclassified: {preview.UnclassifiedCount}
                                        </p>
                                        {preview.ValidationWarnings && preview.ValidationWarnings.length > 0 && (
                                            <div className="mb-2">
                                                {preview.ValidationWarnings.map((w, i) => (
                                                    <p key={i} className="color-red">{w}</p>
                                                ))}
                                            </div>
                                        )}
                                        {preview.AlreadyPublishedCount > 0 && (
                                            <p className="color-red"><b>A Final Result has already been published for this Post ({preview.AlreadyPublishedCount} candidate(s)). Publishing again will replace it.</b></p>
                                        )}

                                        <ButtonComponent
                                            type="button"
                                            value={publishing ? 'Publishing…' : 'Publish Final Result'}
                                            onClick={this.openConfirm}
                                            className="button-global button-primary autoHeight small"
                                            disabled={publishing || preview.PendingCount === 0}
                                        />

                                        {preview.PendingCandidates && preview.PendingCandidates.length > 0 && (
                                            <div className="pt-3">
                                                <table className="table">
                                                    <thead>
                                                        <tr><th>Form Number</th><th>Roll Number</th><th>Name</th><th>Post</th><th>Final Status</th><th>Student Message</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {preview.PendingCandidates.map((c, i) => (
                                                            <tr key={i}>
                                                                <td>{c.FormNumber}</td>
                                                                <td>{c.RollNumber}</td>
                                                                <td>{c.StudentName}</td>
                                                                <td>{preview.PostName}</td>
                                                                <td>{c.PendingFinalStatus || <span className="color-red">Unclassified</span>}</td>
                                                                <td>{c.PendingStudentMessage}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </Card>

                    {this.state.selectedPostId && preview && preview.AlreadyPublishedCount > 0 && (
                        <Card className="mt-3">
                            <h5 className="mb-3">Selected &amp; Waiting Candidates Roll Number List</h5>
                            <p style={{ fontSize: 13 }}>
                                Generates a Roll-Number-wise Selected/Waiting PDF from the <b>published</b> Final Result for <b>{preview.PostName}</b>
                                (never the staged preview above). Reuses the existing Publication mechanism — the same Preview-before-Publish
                                workflow as the Eligible Candidate List, published to the Portal Home Page as one combined document.
                            </p>
                            <div className="mb-3">
                                <ButtonComponent
                                    type="button"
                                    value={generatingPdf ? 'Generating…' : 'Generate & Preview PDF'}
                                    className="button-global button-primary autoHeight small mr-3"
                                    onClick={this.handleGenerateSelectedWaitingPdf}
                                    disabled={generatingPdf}
                                />
                                {pdfBlob && (
                                    <>
                                        <ButtonComponent
                                            type="button"
                                            value="Re-open Preview"
                                            className="button-global button-border autoHeight small mr-3"
                                            onClick={this.handleRePreviewSelectedWaitingPdf}
                                        />
                                        <ButtonComponent
                                            type="button"
                                            value={publishingPdf ? 'Publishing…' : 'Publish Selected/Waiting List'}
                                            className="button-global button-primary autoHeight small"
                                            onClick={this.openSelectedWaitingPublishConfirm}
                                            disabled={publishingPdf}
                                        />
                                    </>
                                )}
                            </div>
                            {rollNumbers && (
                                <div style={{ fontSize: 13 }}>
                                    Generated list contains <b>{rollNumbers.SelectedRollNumbers.length}</b> Selected and{' '}
                                    <b>{rollNumbers.WaitingRollNumbers.length}</b> Waiting Roll Number(s) for <b>{preview.PostName}</b>, not yet published.
                                </div>
                            )}
                        </Card>
                    )}
                </div>

                <Modal
                    centered={true}
                    width={480}
                    className="modelPopAnt admin-home"
                    visible={this.state.showConfirm}
                    onCancel={() => this.setState({ showConfirm: false })}
                    footer={false}
                >
                    {preview && (
                        <ConfirmPopup
                            onCancel={() => this.setState({ showConfirm: false })}
                            onOk={this.handlePublish}
                            message={`Publish the Final Result for ${preview.PostName}? This will make it visible to ${preview.PendingCount} candidate(s) on the portal immediately.${preview.AlreadyPublishedCount > 0 ? ' This will REPLACE the existing published result for ' + preview.AlreadyPublishedCount + ' candidate(s).' : ''}`}
                        />
                    )}
                </Modal>

                <Modal
                    centered={true}
                    width={480}
                    className="modelPopAnt admin-home"
                    visible={this.state.showPdfPublishConfirm}
                    onCancel={() => this.setState({ showPdfPublishConfirm: false })}
                    footer={false}
                >
                    {preview && (
                        <ConfirmPopup
                            onCancel={() => this.setState({ showPdfPublishConfirm: false })}
                            onOk={this.handlePublishSelectedWaitingPdf}
                            message={`Publish the Selected & Waiting Candidates List for ${preview.PostName}? This will make it visible to candidates on the portal home page immediately.${this.getExistingSelectedWaitingPublication() ? ' This will REPLACE the existing published Selected/Waiting list.' : ''}`}
                        />
                    )}
                </Modal>
            </div>
        )
    }
}

export default FinalResult;
